import { Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/core/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/core/components/ui/select';
import type { KioskImageOption } from '../../types/kiosk-flow-types';

// Radix Select items can't use '' as a value
const NO_BANNER = '__none__';

interface BannerSelectProps {
  // File name, e.g. banner_v1.png; '' = no banner
  value: string;
  // The operator's images from Asset Manager — shown by title, picked by file name
  images: KioskImageOption[];
  isLoading: boolean;
  // Images failed to load — the list is unknown, so nothing is flagged as missing
  error?: string | null;
  // Why the banner blocks Save ('save') or Publish ('publish')
  issue?: { message: string; blocks: 'save' | 'publish' };
  disabled?: boolean;
  onChange: (fileName: string) => void;
  onRetry: () => void;
}

// Promo banner dropdown — the kiosk loads the picked file from the operator's images folder by name,
// so the value is the file name while the user sees the Asset Manager title
export function BannerSelect({ value, images, isLoading, error, issue, disabled, onChange, onRetry }: BannerSelectProps) {
  // A saved banner that isn't in the list still shows (by file name — there's no title), so the field keeps its value
  const notListed = value !== '' && !images.some((image) => image.fileName === value);
  const missing = issue?.blocks === 'publish';
  // Titles used by more than one image get their file name alongside, so they can be told apart
  const titleCounts = new Map<string, number>();
  for (const image of images) titleCounts.set(image.title, (titleCounts.get(image.title) ?? 0) + 1);

  return (
    <div className="max-w-md space-y-1">
      <label className="block text-xs font-medium text-gray-600" htmlFor="kiosk-flow-banner">
        Promo banner
      </label>
      <Select
        value={value || NO_BANNER}
        onValueChange={(next) => onChange(next === NO_BANNER ? '' : next)}
        disabled={disabled}
      >
        <SelectTrigger
          id="kiosk-flow-banner"
          className={`h-8 ${issue ? (missing ? 'border-amber-500' : 'border-red-500') : ''}`}
          aria-label="Promo banner"
          aria-invalid={!!issue}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NO_BANNER}>None</SelectItem>
          {notListed && (
            <SelectItem value={value}>
              <span>{value}</span>
              {missing && <span className="ml-2 text-xs text-amber-600">File missing</span>}
            </SelectItem>
          )}
          {images.map(({ fileName, title }) => (
            <SelectItem key={fileName} value={fileName}>
              <span>{title}</span>
              {(titleCounts.get(title) ?? 0) > 1 && title !== fileName && (
                <span className="ml-2 text-xs text-gray-500">{fileName}</span>
              )}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {isLoading && (
        <p className="flex items-center gap-1.5 text-xs text-gray-500">
          <Loader2 className="h-3 w-3 animate-spin" />
          Loading images…
        </p>
      )}
      {!isLoading && error && (
        <p className="flex items-center gap-2 text-xs text-red-600">
          {error}
          <Button variant="outline" size="sm" className="h-6 px-2 text-xs" onClick={onRetry}>
            <RefreshCw className="mr-1 h-3 w-3" />
            Retry
          </Button>
        </p>
      )}
      {!isLoading && !error && images.length === 0 && (
        <p className="text-xs text-gray-500">No images uploaded for this operator</p>
      )}
      {issue && <p className={`text-xs ${missing ? 'text-amber-600' : 'text-red-600'}`}>{issue.message}</p>}
    </div>
  );
}
